import React from 'react';
import { Link, router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreVertical, Eye, Edit, FileText, Trash2 } from 'lucide-react';

interface LineItem {
  id: number;
  description: string;
  hours: number;
  price_per_hour: number;
  total: number;
}

interface Invoice {
  id: number;
  invoice_id: string;
  issue_date: string;
  due_date: string;
  subject?: string;
  from_company: string;
  invoice_for: string;
  amount_due: number;
  status: string;
  line_items: LineItem[];
}

interface InvoiceGroup {
  month: string;
  month_date: string;
  invoices: Invoice[];
}

interface Props {
  invoices_by_month: InvoiceGroup[];
}

export default function Index({ invoices_by_month }: Props) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', text: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-800', text: 'Sent' },
      paid: { color: 'bg-green-100 text-green-800', text: 'Paid' },
      overdue: { color: 'bg-red-100 text-red-800', text: 'Overdue' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const handleDelete = (invoice: Invoice) => {
    if (confirm(`Are you sure you want to delete invoice ${invoice.invoice_id}?`)) {
      router.delete(`/invoices/${invoice.id}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
        <div className="flex gap-2">
          <Link href="/invoices/new">
            <Button>Create New Invoice</Button>
          </Link>
        </div>
      </div>

      {invoices_by_month.length > 0 ? (
        <div className="space-y-8">
          {invoices_by_month.map((group) => {
            const monthTotal = group.invoices.reduce((sum, invoice) => sum + invoice.amount_due, 0);
            return (
            <div key={group.month_date} className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">{group.month}</h2>
                <div className="text-sm">
                  <span className="text-gray-600">Total: </span>
                  <span className="font-semibold text-gray-900">{formatCurrency(monthTotal)}</span>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice ID</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Amount Due</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.invoices.map((invoice) => (
                    <TableRow 
                      key={invoice.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => router.visit(`/invoices/${invoice.id}`)}
                    >
                      <TableCell className="font-medium">{invoice.invoice_id}</TableCell>
                      <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell>{invoice.invoice_for}</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="font-medium">{formatCurrency(invoice.amount_due)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 ml-auto"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="sr-only">Open menu</span>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem asChild>
                              <Link href={`/invoices/${invoice.id}`} className="flex items-center cursor-pointer">
                                <Eye className="mr-2 h-4 w-4" />
                                View
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link href={`/invoices/${invoice.id}/edit`} className="flex items-center cursor-pointer">
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={`/invoices/${invoice.id}.pdf`} download className="flex items-center cursor-pointer">
                                <FileText className="mr-2 h-4 w-4" />
                                Download PDF
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(invoice)}
                              className="text-red-600 focus:text-red-600 cursor-pointer"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg">
          <div className="text-center py-12">
            <p className="text-gray-500">No invoices yet. Create your first invoice!</p>
          </div>
        </div>
      )}
    </div>
  );
}